import { useAssignInterviewMutation } from "@/app/features/recruitment/interview/interview.api";
import { cn } from "@/lib/cn";
import {
  useGetAIGeneratedInterviewQuestionsQuery,
  useGetShortListedApplicantsQuery,
} from "@/app/features/recruitment/job-applicants/job-applicants.api";
import IMAGES from "@/assets/images";
import CustomDrawer from "@/components/CustomDrawer";
import CustomImage from "@/components/CustomImage";
import type { CustomModalProps } from "@/components/CustomModal";
import WithLoader from "@/components/WithLoader";

import {
  BriefcaseIcon,
  Calendar03Icon,
  Call02Icon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  MailIcon,
  TrashIcon,
  XIcon,
  CirclePlusIcon,
} from "@/icons";
import { getErrorMessage } from "@/lib/error";
import { toast } from "@/lib/toast";
import {
  DATE_FORMATS,
  disablePastTimes,
  formatDate,
  formatTime,
} from "@/utils";
import {
  Button,
  Calendar,
  Card,
  DatePicker,
  Divider,
  Flex,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
} from "antd";
import TextArea from "antd/es/input/TextArea";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";

type Props = CustomModalProps & { id?: string; data?: any };

export default function SetupInterview({ open, handleClose, data }: Props) {
  const [form] = Form.useForm();
  const interviewDate = Form.useWatch("interview_date", form);
  const interviewMethod = Form.useWatch("interview_method", form);
  const interviewType = Form.useWatch("interview_type", form);
  const selectedApplicantId = Form.useWatch("job_application_id", form);

  const preSelectedApplicants = data?.map((item: any) => ({
    value: item?.id,
    label: `${item.applicant_name} - ${item.job_title || item.designation_name}`,
  }));

  const { data: shortlistedResponse, isFetching: isApplicantsFetching } =
    useGetShortListedApplicantsQuery(
      { params: { interview_status: "pending", page: 1, limit: 1000 } },
      { skip: (preSelectedApplicants?.length ?? 0) > 1 },
    );
  const shortListedApplicants = shortlistedResponse?.data?.data;

  const applicantOptions = useMemo(() => {
    return (
      shortListedApplicants?.map((item) => ({
        value: item.id,
        label: `${item.applicant_name} - ${item.job_title || item.designation_name}`,
      })) || []
    );
  }, [shortListedApplicants]);

  const applicant = useMemo(() => {
    if (data) {
      return data?.find((a: any) => a?.id === selectedApplicantId);
    }
    return shortListedApplicants?.find(
      (a: any) => a?.id === selectedApplicantId,
    );
  }, [shortListedApplicants, data, selectedApplicantId]);
  const jobId = applicant?.job_id;

  const [pollingInterval, setPollingInterval] = useState(0);

  const { data: aiQuestionsData, isFetching: isAiQuestionsFetching } =
    useGetAIGeneratedInterviewQuestionsQuery(jobId!, {
      skip: !jobId || !open,
      pollingInterval,
    });

  useEffect(() => {
    if (aiQuestionsData?.data?.interview_qus_status === "pending") {
      setPollingInterval(3000);
    } else {
      setPollingInterval(0);
    }
  }, [aiQuestionsData]);

  // Sync form fields whenever the interview type or AI questions data changes
  useEffect(() => {
    if (interviewType === "ai") {
      form.setFieldsValue({
        meeting_link: `https://hrxbd.org/job-portal/interview/${applicant?.id}`,
        questions: aiQuestionsData?.data?.questions?.map((q: any) => ({
          question: q.question,
          answer: q.answer,
          ai_question_id: String(q.question_id),
          mark: 100,
        })),
      });
    } else {
      form.setFieldsValue({
        meeting_link: "",
        questions: aiQuestionsData?.data?.questions?.map((q: any) => ({
          question: q.question,
          mark: 100,
        })),
      });
    }
  }, [interviewType, applicant, aiQuestionsData, form]);

  const [assignInterview, { isLoading: isAssigning }] =
    useAssignInterviewMutation();

  const handleSubmit = async (values: any) => {
    const payload: any = {
      location: values?.location,
      meeting_link: values?.meeting_link,
      interview_method: values?.interview_method,
      job_application_id: values?.job_application_id,
      interview_date: formatDate(values?.interview_date, DATE_FORMATS.ISO_DATE),
      interview_time: formatTime(values?.interview_time, "TIME_24H"),
      questions: values?.questions.map((q: any) => ({
        question: q.question,
        answer: q.answer,
        ai_question_id: q.ai_question_id,
        mark: q.mark || 100,
      })),
    };

    if (values?.interview_method !== "onsite") {
      payload.interview_type = values?.interview_type;
    }

    try {
      const res = await assignInterview(payload).unwrap();
      if (res?.success === false) {
        toast("error", getErrorMessage(res, "Failed to schedule interview"));
        return;
      }
      toast(
        "success",
        getErrorMessage(res, "Interview scheduled successfully"),
      );
      form.resetFields();
      handleClose();
    } catch (error) {
      toast("error", getErrorMessage(error, "Failed to schedule interview"));
    }
  };

  return (
    <CustomDrawer
      width={850}
      open={open}
      onClose={() => {
        handleClose();
        form.resetFields();
      }}
      title={"Set Up Interview"}
    >
      <Form
        form={form}
        size="large"
        layout="vertical"
        className="custom-form space-y-6"
        onFinish={handleSubmit}
        initialValues={{
          interview_date: dayjs(),
          interview_method: "online",
          // questions: [{ question_set: '', mark: '' }],
        }}
      >
        <Card
          className="dark:bg-theme-slate-800 border dark:border-theme-gray-700 rounded-xl text-white shadow-md"
          styles={{ body: { padding: "22px" } }}
          loading={false}
        >
          <Form.Item
            label="Assign To"
            name="job_application_id"
            rules={[
              {
                required: true,
                message: "Please select assign to",
              },
            ]}
          >
            <Select
              showSearch
              allowClear
              placeholder="Select"
              optionFilterProp="label"
              options={preSelectedApplicants || applicantOptions}
              className="transparent-select"
              disabled={isApplicantsFetching}
              suffixIcon={<ChevronDownIcon size={24} className="text-white" />}
            />
          </Form.Item>

          {applicant && (
            <div className="my-3 grid grid-cols-2 md:grid-cols-3 gap-3  mt-5">
              <Flex
                align="start"
                gap={10}
                className="p-2.5 rounded-lg bg-[#0f172a] relative"
              >
                <Space>
                  <CustomImage
                    alt="User"
                    width={25}
                    height={25}
                    src={IMAGES.avatar}
                    className="rounded-full"
                  />
                </Space>
                <div className="flex-1 min-w-0">
                  <Space className="w-full">
                    <div className="text-white text-base font-medium truncate">
                      {applicant?.applicant_name}
                    </div>
                  </Space>
                  <Space size={8} className="w-full">
                    <MailIcon size={12} className="text-white" />
                    <div className="text-white text-xs font-normal truncate">
                      {applicant?.email}
                    </div>
                  </Space>

                  <Space size={8} className="w-full">
                    <Call02Icon size={12} className="text-white" />
                    <div className="text-white text-xs font-normal truncate">
                      {applicant?.phone}
                    </div>
                  </Space>

                  <Space size={8} className="w-full">
                    <BriefcaseIcon size={12} className="text-theme-primary" />
                    <div className="text-white text-xs font-normal w-40 truncate">
                      {applicant?.job_title ||
                        applicant?.designation_name ||
                        "N/A"}
                    </div>
                  </Space>
                </div>
                <button
                  type="button"
                  onClick={() => form.resetFields(["job_application_id"])}
                  className="absolute -right-1.5 -top-1.5 p-1 bg-theme-primary rounded-full z-10"
                >
                  <XIcon size={12} strokeWidth={3} className="text-white" />
                </button>
              </Flex>
            </div>
          )}

          <Divider className="border-slate-600" />

          {/* Ant Design Calendar */}
          <div className="w-full dark:bg-theme-slate-800  text-white p-3">
            <div className="flex items-start justify-center mb-4 text-xl font-semibold ">
              <button
                type="button"
                onClick={() => {
                  form.setFieldValue(
                    "interview_date",
                    interviewDate.subtract(1, "month"),
                  );
                }}
                className="px-2 text-gray-300 hover:text-white text-base mt-2"
              >
                <ChevronLeftIcon className="size-8" />
              </button>

              <div className="flex-col justify-center text-center">
                <div>{interviewDate?.format("MMMM")}</div>
                <div className="text-white text-sm font-bold">
                  {interviewDate?.format("YYYY")}
                </div>
              </div>

              <button
                type="button"
                onClick={() => {
                  form.setFieldValue(
                    "interview_date",
                    interviewDate.add(1, "month"),
                  );
                }}
                className="px-2 text-gray-300 hover:text-white text-base mt-2"
              >
                <ChevronRightIcon className="size-8" />
              </button>
            </div>

            {/* Ant Design Calendar */}
            <Calendar
              value={interviewDate}
              fullscreen={false}
              headerRender={() => null}
              className="custom-calendar"
              disabledDate={(current) =>
                current.isBefore(dayjs().startOf("day"))
              }
              onSelect={(value) => form.setFieldValue("interview_date", value)}
              onPanelChange={(val) => form.setFieldValue("interview_date", val)}
            />
          </div>

          <div className="grid grid-cols-2 gap-6">
            <Form.Item
              label="Interview Method"
              name="interview_method"
              rules={[
                {
                  required: true,
                  message: "Please select interview method",
                },
              ]}
            >
              <Select
                placeholder="Select"
                className="transparent-select"
                suffixIcon={
                  <ChevronDownIcon size={24} className="text-white" />
                }
                options={[
                  { label: "Online", value: "online" },
                  { label: "Onsite", value: "onsite" },
                ]}
              />
            </Form.Item>
            {interviewMethod !== "onsite" && (
              <Form.Item
                label="Interview Type"
                name="interview_type"
                initialValue={"manual"}
                rules={[
                  {
                    required: true,
                    message: "Please select interview type",
                  },
                ]}
              >
                <Select
                  placeholder="Select"
                  className="transparent-select"
                  suffixIcon={
                    <ChevronDownIcon size={24} className="text-white" />
                  }
                  options={[
                    { label: "Manual Interview", value: "manual" },
                    { label: "AI Interview", value: "ai" },
                  ]}
                />
              </Form.Item>
            )}
          </div>

          {interviewMethod === "online" ? (
            <Form.Item
              label="Online Meeting Link"
              name="meeting_link"
              rules={[
                {
                  required: true,
                  message: "Please enter online meeting link",
                },
              ]}
            >
              <Input
                readOnly={interviewType === "ai"}
                disabled={interviewType === "ai"}
                className="transparent-input"
              />
            </Form.Item>
          ) : (
            <Form.Item
              label="Location"
              name="location"
              rules={[
                {
                  required: true,
                  message: "Please enter interview location",
                },
              ]}
            >
              <Input className="transparent-input" />
            </Form.Item>
          )}

          <div className="grid grid-cols-2 gap-6">
            <Form.Item
              label="Interview Date"
              name="interview_date"
              rules={[
                {
                  required: true,
                  message: "Please select interview date",
                },
              ]}
            >
              <DatePicker
                disabled
                picker="date"
                format="DD MMM, YYYY"
                placeholder="DD MMM, YYYY"
                style={{ width: "100%" }}
                className="transparent-picker"
                suffixIcon={<Calendar03Icon />}
              />
            </Form.Item>

            <Form.Item
              label="Select Interview Time"
              name="interview_time"
              rules={[
                {
                  required: true,
                  message: "Please select interview time",
                },
              ]}
            >
              <DatePicker
                picker="time"
                format="hh:mm A"
                placeholder="HH:MM"
                showNow={false}
                needConfirm={false}
                style={{ width: "100%" }}
                className="transparent-picker"
                disabledTime={disablePastTimes}
              />
            </Form.Item>
          </div>

          <div>
            <div className="justify-start pb-3 border-b border-slate-600 mt-5">
              <span className="text-white text-base font-normal">
                Set Interview Question Set & Mark{" "}
              </span>
              <span className="text-rose-500 text-base font-normal">*</span>
            </div>
            <Form.List name="questions">
              {(fields, { add, remove }) => (
                <WithLoader
                  isLoading={isAiQuestionsFetching && interviewType === "ai"}
                  className={cn(
                    "rounded-lg",
                    isAiQuestionsFetching &&
                      interviewType === "ai" &&
                      "min-h-60",
                  )}
                >
                  {fields.map(({ key, name, ...restField }) => {
                    const question = form.getFieldValue([
                      "questions",
                      name,
                      "question",
                    ]);
                    const mark = form.getFieldValue([
                      "questions",
                      name,
                      "mark",
                    ]);

                    return (
                      <Flex
                        key={key}
                        justify="space-between"
                        gap={16}
                        className="mt-5"
                        align="start"
                      >
                        <div className="pt-1">
                          <div className="text-theme-slate-900 bg-theme-primary font-bold p-1 rounded-full shrink-0 size-7 flex items-center justify-center">
                            {name + 1}
                          </div>
                        </div>

                        {interviewType === "ai" ? (
                          <>
                            <Form.Item
                              {...restField}
                              name={[name, "answer"]}
                              hidden
                              noStyle
                            />
                            <Form.Item
                              {...restField}
                              name={[name, "ai_question_id"]}
                              hidden
                              noStyle
                            />
                            <Form.Item
                              {...restField}
                              name={[name, "question"]}
                              hidden
                              noStyle
                            />
                            <Form.Item
                              {...restField}
                              name={[name, "mark"]}
                              hidden
                              noStyle
                            />
                            <div className="text-white px-3 py-2 rounded-lg bg-theme-slate-900 border border-theme-slate-700 flex-1">
                              {question}
                            </div>
                            <div className="text-white px-3 py-2 rounded-lg bg-theme-slate-900 border border-theme-slate-700 w-20 text-center">
                              {mark}
                            </div>
                          </>
                        ) : (
                          <>
                            <Form.Item
                              {...restField}
                              name={[name, "question"]}
                              className="flex-1 mb-0"
                              rules={[
                                {
                                  required: true,
                                  message: "Please enter question",
                                },
                              ]}
                            >
                              <TextArea
                                placeholder="Enter question"
                                className="transparent-input"
                              />
                            </Form.Item>
                            <Form.Item
                              {...restField}
                              name={[name, "mark"]}
                              className="w-24 mb-0"
                              rules={[
                                { required: true, message: "Empty" },
                                {
                                  min: 1,
                                  max: 100,
                                  type: "number",
                                  message: "1-100",
                                },
                              ]}
                            >
                              <InputNumber
                                placeholder="Mark"
                                className="transparent-input w-full"
                              />
                            </Form.Item>
                            <button
                              type="button"
                              onClick={() => remove(name)}
                              className="pt-2 text-rose-500 hover:text-rose-400 transition-colors"
                            >
                              <TrashIcon size={20} />
                            </button>
                          </>
                        )}
                      </Flex>
                    );
                  })}

                  {(interviewMethod === "onsite" ||
                    interviewType === "manual") && (
                    <button
                      type="button"
                      onClick={() => add({ question: "", mark: 100 })}
                      className="w-full flex items-center mt-5 group"
                    >
                      <div className="flex-1 border-b border-dashed border-theme-primary/30 group-hover:border-theme-primary transition-colors" />

                      <div className="mx-3 bg-theme-slate-800 z-10">
                        <CirclePlusIcon
                          size={24}
                          className="text-theme-primary hover:scale-110 transition-transform"
                        />
                      </div>

                      <div className="flex-1 border-b border-dashed border-theme-primary/30 group-hover:border-theme-primary transition-colors" />
                    </button>
                  )}
                </WithLoader>
              )}
            </Form.List>
          </div>

          {/* <div className="flex justify-end">
            <div className="p-2.5 rounded-lg outline outline-1 outline-offset-[-1px] outline-gray-700 inline-flex justify-end items-start gap-10 mt-5">
              <div className="justify-start text-white text-lg font-normal">
                Total Mark
              </div>
              <div className="justify-start text-teal-500 text-xl font-bold">
                100
              </div>
            </div>
          </div> */}
        </Card>

        <div className="flex justify-end">
          <Button
            className="btn-primary min-w-20"
            htmlType="submit"
            loading={isAssigning}
          >
            Save
          </Button>
        </div>
      </Form>
    </CustomDrawer>
  );
}
